import { CandleData, Timeframe } from "../types";

const getMarketBaseUrl = () => {
  // Dev uses Vite proxy for /api/*
  if (import.meta.env.DEV) return '';

  // Production: explicitly configured backend URL
  return import.meta.env.VITE_API_URL || '';
};

/**
 * Fetch candle data from backend market API
 * 
 * @param ticker Symbol to fetch (e.g., 'BTCUSDT' or 'AAPL')
 * @param interval Timeframe (e.g., '1h', '4h')
 * @param endTime Optional timestamp (ms) - ONLY for pagination/replay mode!
 *                For LIVE mode, do NOT pass this parameter to get the most recent data.
 *                If provided, must be in the past (< Date.now())
 */
export const fetchCandleData = async (ticker: string, interval: Timeframe = '1h', endTime?: number): Promise<{ data: CandleData[], source: string }> => {
  const cleanTicker = ticker.toUpperCase().trim();
  // const isCrypto = cleanTicker.includes('USDT') || cleanTicker.includes('USDC');
  
  // Removed legacy port 8000 fetch. We now use the unified /api/market endpoint which handles Schwab priority.

  try {
    // Updated to match backend API structure: /api/market/candles/:symbol
    const baseUrl = getMarketBaseUrl();
    let url = `${baseUrl}/api/market/candles/${cleanTicker}?timeframe=${interval}&limit=100`;
    
    // IMPORTANT: Only use endTime for pagination/replay, NOT for live mode
    // endTime should only be used when loading MORE history (pagination)
    if (endTime) {
      // Validate: endTime should be in the past, not future
      const now = Date.now();
      if (endTime < now) {
        url += `&endTime=${endTime}`;
        console.log(`[MarketService] Using endTime for pagination: ${new Date(endTime).toISOString()}`);
      } else {
        console.warn(`[MarketService] ⚠️ Ignoring invalid endTime (future): ${new Date(endTime).toISOString()}`);
        console.warn(`[MarketService] For LIVE mode, do NOT pass endTime!`);
      }
    }
    
    console.log(`[MarketService] Fetching ${cleanTicker} (${interval}) from backend...`);
    
    // Always fetch fresh data - disable browser caching for live trading
    const response = await fetch(url, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      }
    });
    const source = response.headers.get('X-Data-Source') || 'unknown';
    
    console.log(`[MarketService] ✅ Data source: ${source.toUpperCase()}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Market Data Error: ${response.statusText} - ${errorText}`);
    }
    
    const candles = await response.json();
    
    if (!Array.isArray(candles)) {
      throw new Error("Invalid data format received from backend");
    }

    // Ensure dateStr is present (backend sends time, open, high, low, close, volume)
    const data = candles.map((c: any) => ({
      ...c,
      // Add formatted time string for frontend display if not already present
      dateStr: c.dateStr || new Date(c.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
    }));

    // Deduplicate by timestamp to prevent React key errors
    const seen = new Set<number>();
    const dedupedData = data.filter((candle: CandleData) => {
      if (seen.has(candle.time)) {
        return false;
      }
      seen.add(candle.time);
      return true;
    });

    if (data.length !== dedupedData.length) {
      console.warn(`[MarketService] Removed ${data.length - dedupedData.length} duplicate candles from ${cleanTicker}`);
    }

    // Log volume stats for debugging
    if (dedupedData.length > 0) {
      const volumes = dedupedData.map((c: CandleData) => c.volume);
      const maxVolume = Math.max(...volumes);
      const avgVolume = volumes.reduce((a, b) => a + b, 0) / volumes.length;
      const maxVolumeCandle = dedupedData.find((c: CandleData) => c.volume === maxVolume);
      console.log(`[MarketService] 📊 Volume stats for ${cleanTicker}:`);
      console.log(`  - Avg: ${(avgVolume / 1000000).toFixed(2)}M`);
      console.log(`  - Max: ${(maxVolume / 1000000).toFixed(2)}M at ${new Date(maxVolumeCandle!.time).toLocaleString()}`);
    }

    return { data: dedupedData, source };

  } catch (error) {
    console.error("Fetch Candle Data Error:", error);
    throw error;
  }
};

export const fetchTickData = async (ticker: string, from: number, to: number): Promise<any[]> => {
    const cleanTicker = ticker.toUpperCase().trim();
    try {
        // Updated to match backend API structure: /api/market/ticks/:symbol
        const baseUrl = getMarketBaseUrl();
        const url = `${baseUrl}/api/market/ticks/${cleanTicker}?start=${from}&end=${to}`;
        console.log(`[MarketService] Fetching ticks for ${cleanTicker} (${from}-${to})`);
        
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Tick Data Error: ${response.statusText}`);
        }
        const data = await response.json();
        
        // Backend returns { symbol, start, end, count, provider, ticks }
        // Extract the ticks array
        const ticks = data.ticks || [];
        console.log(`[MarketService] ✅ Received ${ticks.length} ticks from ${data.provider || 'unknown'}`);
        
        return ticks;
    } catch (error) {
        console.error("Fetch Tick Data Error:", error);
        return []; // Return empty on error to allow graceful fallback
    }
};